/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { PALSession } from '../PALSession';
import { DEFAULT_PAL_CONFIGURATION, PALConfiguration } from '../model/PALConfiguration';
import { createError, createLogger, Error, ErrorCodes, Logger, LoggerLevel, UtilFactory } from '@quickplay/foundation';
import { PALErrorCodes } from './PALErrorCodes';

/**
 * CastPALSessionImpl class
 *
 * This class provides an implementation of the PAL
 * session specifically for Chromecast devices. It facilitates the integration of
 * features and functionalities that are essential for running applications on Chromecast.
 */
declare const goog: any;

const TAG_NAME = 'FL_PAL_SESSION';
export class CastPALSessionImpl implements PALSession {
  private deferred_: any;
  /**
   * Provides access to the logger.
   * @property {Logger} logger
   */
  private logger: Logger;
  /**
   * Provides access to the nonce loader.
   * @property {any} nonceLoader
   */
  private nonceLoader: any;
  /**
   * Provides access to the nonce manager.
   * @property {any} nonceManager
   */
  private nonceManager: any;

  /**
   * Creates a new nonce loader.
   * @param allowStorage whether PAL has purpose 1 consent to use identifiers and storage. The value needs to be
   * determined by integrating with CMP (Consent Management Provider). This is set to false by default. If specified
   * as true, PAL has consent from the user to use information like cookies, device IDs and advertising IDs. This
   * value needs to be set to be IAB TCFv2 (Transparency and Consent Framework) compliant.
   * @param logger
   */
  constructor(allowStorage: boolean, logger?: Logger) {
    this.logger = logger || createLogger(LoggerLevel.OFF, TAG_NAME);

    this.nonceLoader = this.createNonceLoader(allowStorage);

    this.logger.log(`PAL session created.`);
  }

  async initialize(palConfig: PALConfiguration): Promise<string> {
    this.deferred_ = new UtilFactory.Deferred<string>();

    const nonceRequest = this.createNonceRequest(palConfig);

    try {
      const manager = await this.nonceLoader.loadNonceManager(nonceRequest);

      this.logger.log(`PAL session initialized.`);

      this.nonceManager = manager;

      this.deferred_.resolve(manager.getNonce());
    } catch (error) {
      const message = `PAL session initialization failed. ${error}`;

      this.logger.warn(message);

      // TODO: set a valid error code
      const error_ = this.createSessionError(-1, message);

      this.deferred_.reject(error_);
    }

    return this.deferred_.promise;
  }

  sendAdClick(): void {
    this.nonceManager.sendAdClick();
  }

  sendAdTouch(clickOrTouchEvent: any): void {
    this.nonceManager.sendAdTouch(clickOrTouchEvent);
  }

  sendPlaybackStart(): void {
    this.nonceManager.sendPlaybackStart();
  }

  sendPlaybackEnd(): void {
    this.nonceManager.sendPlaybackEnd();
  }

  shutdown(): void {
    this.logger.log('PAL session destroyed.');

    this.nonceLoader = undefined;
    this.nonceManager = undefined;
  }

  /**
   * Creates a new instance of PAL Nonce Loader.
   * @param allowStorage
   * @private
   */
  private createNonceLoader(allowStorage: boolean): any {
    const consentSettings = new goog.cast.pal.ConsentSettings();

    consentSettings.allowStorage = allowStorage;

    return new goog.cast.pal.NonceLoader(consentSettings);
  }

  /**
   * Creates a new instance of PAL Nonce Request.
   * @param customConfig the request information
   * @private
   */
  private createNonceRequest(customConfig: PALConfiguration): any {
    const palConfig = {
      ...DEFAULT_PAL_CONFIGURATION,
      ...customConfig,
    };

    const nonceRequest = new goog.cast.pal.NonceRequest();

    // set PAL default value if adWillAutoPlay is not defined
    if (palConfig.adWillAutoPlay) nonceRequest.adWillAutoPlay = palConfig.adWillAutoPlay;
    nonceRequest.adWillPlayMuted = palConfig.adWillPlayMuted;
    nonceRequest.continuousPlayback = palConfig.continuousPlayback;
    nonceRequest.descriptionUrl = palConfig.descriptionUrl;
    nonceRequest.iconsSupported = palConfig.iconsSupported;
    // set PAL default value if nonceLengthLimit is not defined
    if (palConfig.nonceLengthLimit) nonceRequest.nonceLengthLimit = palConfig.nonceLengthLimit;
    // set PAL default value if omidPartnerName is not defined
    if (palConfig.omidPartnerName) nonceRequest.omidPartnerName = palConfig.omidPartnerName;
    // set PAL default value if omidPartnerVersion is not defined
    if (palConfig.omidPartnerVersion) nonceRequest.omidPartnerVersion = palConfig.omidPartnerVersion;
    // set PAL default value if omidVersion is not defined
    if (palConfig.omidVersion) nonceRequest.omidVersion = palConfig.omidVersion;
    nonceRequest.playerType = palConfig.playerType;
    nonceRequest.playerVersion = palConfig.playerVersion;
    nonceRequest.ppid = palConfig.ppid;
    nonceRequest.videoHeight = palConfig.videoHeight;
    nonceRequest.videoWidth = palConfig.videoWidth;

    return nonceRequest;
  }

  /**
   * Creates an error object containing session initialization failure details.
   * @param errorCode the internal error code
   * @param message the internal error message
   */
  private createSessionError = (errorCode: number, message: string): Error => {
    const internalError = createError(errorCode, message);

    return createError(
      PALErrorCodes.SESSION_INIT_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
      message,
      internalError,
    );
  };
}
